import  {
  Card,
  CardContent,
  CardMedia,
  Grid,
  Link,
  Tooltip,
  Typography
} from '@mui/material';
import React, { memo, useCallback } from 'react';
import { camelCase, cloneDeep, startCase } from 'lodash';
import { green, grey, lightGreen, red } from '@mui/material/colors';

import MuiCheckbox from '@mui/material/Checkbox';
import NativeCheckbox from 'shared/components/NativeCheckbox';
import ProgressBar from 'shared/components/ProgressBar';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import numeral from 'numeral';
import styled from 'styled-components';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: '100%',
    height: 79,
    border: '0 !important',
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
  },
  indentText: {
    marginLeft: theme.spacing(1),
  },
  content: {
    flex: '1 0 auto',
  },
  cover: {
    width: 70,
    height: 70,
    margin: theme.spacing(1),
    padding: 40
  },
  cardContent: {
    padding: 0,
  },
  midSection: {
    backgroundColor: grey[200],
  },
  dueColor: {
    color: green[500]
  },
  overdueColor: {
    color: red[500]
  },
  test: {
    backgroundColor: red[500]
  },
  gridContainerMargin: {
    marginBottom: -1,
    marginLeft: 16,
    marginRight: 16,
    backgroundColor: '#fff',
    boxShadow: '0 4x 4px 0 #888;',
    width: 'calc(100% - 32px)',
    borderBottom: '1px solid white',
  },
  gridContainerMarginPortfolio: {
    marginBottom: -1,
    marginLeft: 16,
    marginRight: 16,
    backgroundColor: '#fff',
    boxShadow: '0 4px 4px 0 #888;',
    width: 'calc(100% - 32px)',
    borderBottom: '1px solid white',
  },
  gridContainerMarginLast: {
    marginBottom: -1,
    marginLeft: 16,
    marginRight: 16,
    backgroundColor: '#fff',
    width: 'calc(100% - 32px)',
    borderBottom: '1px solid white',
    boxShadow: '0 4px 4px 0 #ccc;',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  customGridItem: {
    textAlign: 'center',
  },
  pointer: {
    cursor: 'pointer',
    color: '#1890ff',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,.1)',
    },
  },
  topRow: {
    maxWidth: '200%',
  },
  progress: {
    padding: '0 40px !important',
    alignSelf: 'flex-end',
  },
  tightLines: {
    lineHeight: '18px',
  },
  '@global': {
    '.MuiCardContent-root:last-child': {
      padding: 0,
      width: 'calc(100% - 70px)',
    }
  }
}));


const MidSectionAllocated = ({ donated, budget, claimPercentage, onOpenDrawer }) => {
  const classes = useStyles();

  return (
    <Tooltip title="View Donation" placement="top" >
      <Grid 
        container
        direction="column"
        justify="center"
        alignItems="center"
        className={classes.pointer}
        onClick={onOpenDrawer}
      >
        <Grid item>
          <BigText>
            ${donated} / ${budget}
          </BigText>
        </Grid>
        <Grid item>
          Donated / Budget
        </Grid>
        <Grid item>
          <BigText>
            {claimPercentage} Claim
          </BigText>
        </Grid>
      </Grid>
    </Tooltip>
  );
};

const MidSectionNotAllocated = ({ cash, goods, volunteerism, services, onOpenDrawer }) => {
  const total = cash + goods + volunteerism + services;
  const classes = useStyles();

  return (
    <Tooltip title="View Donation" placement="top" >
      <Grid
        container
        direction="row"
        justify="center"
        alignItems="center"
        onClick={onOpenDrawer}
        className={classes.pointer}
      >
        <Grid item xs={3}>
          <BigText>
            {`$${numeral(total).format('0a')}`}
          </BigText>
        </Grid>
        <Grid item xs={9}>
          <Grid 
            container
            direction="column"
            justify="center"
            alignItems="center"
            className={classes.tightLines}
          >
            <Grid item>
              {`$${numeral(cash).format('0a')} Cash`}
            </Grid>
            <Grid item>
              {`$${numeral(goods).format('0a')} Goods`}
            </Grid>
            <Grid item>
              {`$${numeral(volunteerism).format('0a')} Volunteerism`}
            </Grid>
            <Grid item>
              {`$${numeral(services).format('0a')} Services`}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Tooltip>
  );
};

const RightSectionOverdue = ({
  dueDate,
  taskType,
  firstName,
  lastName,
  email,
  phone,
}) => (
  <Grid 
    container
    direction="column"
    justify="center"
    alignItems="flex-start"
  >
    <Grid item>
      <BigText>
        Task Overdue -- {taskType}
      </BigText>
      <Grid item>
        <Typography variant="subtitle1">
          Due - {dueDate} ({daysAgo(dueDate)} days ago)
        </Typography>
      </Grid>  
      <Grid item>
        <Typography variant="subtitle1">
          <a
            target="_blank"
            href={`mailto:${email}`}
          >
            {firstName} {lastName}
          </a>
          {phone}
        </Typography>
      </Grid>  
    </Grid>
  </Grid>
);

const RightSectionAllocated = ({ title, reached }) => (
  <Grid 
    container
    direction="column"
    justify="center"
    alignItems="flex-start"
  >
    <Grid item>
      <BigText noWrap>
        {title}
      </BigText>
    </Grid>
    {reached
      ? <Grid item>
          <Typography variant="subtitle1">
            Reached: {numeral(reached).format(0, 0)}
          </Typography>
        </Grid>
      : null}
  </Grid>
);

const RightSectionNotAllocated = ({ date, allocationDue, due, days }) => {
  const classes = useStyles();
  
  return (
    <Grid
      container
      direction="column"
      justify="center"
      alignItems="flex-start"
    >
      <Grid item>
        <BlueText>
          Sent: {date}
        </BlueText>
      </Grid>
      {allocationDue && (
        <Grid item>
          <Typography component="subtitle1" variant="subtitle1">
            Allocation Due: {allocationDue}
          </Typography>
        </Grid>)}
      {days && (
        <Grid item>
          <Typography component="subtitle1" variant="subtitle1" className={due === 'overdue' ? classes.overdueColor : classes.dueColor}>
            ({days} days {due})
          </Typography>
        </Grid>)}
    </Grid>
  );
};

const RowCardDetail = memo(({
  toprow,
  middlerow,
  bottomrow,
  cash,
  goods,
  volunteerism,
  services,
  progress,
  openNewDonationDrawer,
  openPortfolioDrawer,
  openImpactReceiptDrawer,
  investmentId,
  granteeName,
  year,
  totalBudgetInitial,
  donationMessage,
  selectedRows,
  setSelectedRows,
  group,
  investmentTotal,
  allocationAmount,
  claimPercentage,
  row,
  totalReports = 0,
  groupKey,
  isLastRow,
  defaultLogoUrl,
}) => {
  const {
    overdueTask, status, amount, programName,
    relationshipManagerId, portfolioId, program,
    allocationCreatedAt, invitationCreatedAt,
    report,
  } = row;
  const logoFileUrl = row?.logoFileUrl ?? defaultLogoUrl;
  const classes = useStyles();

  const handleOpenDrawer = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const allocations = [];
    allocations.push({
      programName,
      amount,
      granteeName,
      year,
      totalBudgetInitial
    });

    openNewDonationDrawer({
      id: investmentId,
      allocations,
      granteeName,
      donationMessage,
      portfolio: portfolioId,
      manager: relationshipManagerId || 0,
      cash,
      goods,
      volunteerism,
      services,
      tags: [],
      status,
      programName: row?.programName ?? '-',
      programNameByDonor: row?.programNameByDonor,
      invitationStatus: getInvitationStatus(row)
    });
  };

  const handleChangeCheckbox = e => {
    const { checked } = e.target;
    setSelectedRows(oldSelection => {
      const selected = oldSelection[groupKey] ?? [];
      return ({
        ...oldSelection,
        [groupKey]: checked
          ? [...selected, row]
          : selected
            .filter(({ allocationId, investmentId }) =>
              row.allocationId !== allocationId || row.investmentId !== investmentId),
      });
    });
  };

  const handleOpenPortfolio = useCallback(e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    openPortfolioDrawer(e, portfolioId);
  }, [portfolioId]);

  const handleOpenIR = useCallback(e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!investmentId) return;
    openImpactReceiptDrawer(toprow, investmentId);
  }, [toprow, investmentId]);

  const checked = selectedRows[groupKey]
    ?.some(({ allocationId, investmentId }) => row.allocationId === allocationId && row.investmentId === investmentId);
  console.log('RowCardDetail', toprow, row.allocationId, row.investmentId);

  const isAllocated = Boolean(totalReports);
  const leftSection = {
    toprow,
    middlerow,
    bottomrow,
    progressValue: progress?.value,
    progressColor: progress?.color,
  };
  const allocatedMidSection = {
    donated: numeral(allocationAmount).format('0a'),
    budget: numeral(investmentTotal).format('0a'),
    claimPercentage,
  };
  const notAllocatedMidSection = {
    cash,
    goods,
    volunteerism,
    services
  };

  const allocatedRightSection = {
    title: report?.serviceName ?? 'No service selected yet',
    reached: /*report?.serviceName && */Math.round(report?.totalReached),
  };
  const taskDueDate = overdueTask?.dueDate;

  const utcOffset = moment().utcOffset();

  const notAllocatedRightSection = {
    date: invitationCreatedAt ?? allocationCreatedAt
      ? moment(invitationCreatedAt ?? allocationCreatedAt).add(utcOffset, 'minutes').format('MM Do YYYY, h:mm:ss a')
      : 'Not Sent',
    allocationDue: taskDueDate && moment(taskDueDate).format('MM Do YYYY'),
    days: taskDueDate && Math.round(Math.abs(Date.now() - new Date(taskDueDate).valueOf() / 24 * 60 * 60 * 1000)),
    due: overdueTask ? 'overdue' : 'until due',
  };

  const isPortfolioGroup = (group === 'by-portfolio');

  return (
    <Container {...{ isLastRow }}> 
      <Grid item className={classes.customGridItem}>
        <CheckboxWithIndent
          checked={checked}
          onChange={handleChangeCheckbox}
        />
      </Grid>
      <CardMain>
        <Card className={classes.root}>
          {logoFileUrl
            ? <RowCardMedia
                className={classes.cover}
                image={logoFileUrl}
                title="Logo"
              />
            : <NoLogo className={classes.cover}/>}
          <RowCardContent>
            <Grid 
              container
              direction="row"
              justify="space-between"
              alignItems="stretch"
              spacing={1}
            > 
              <Tooltip title={isAllocated ? 'View Impact Reciept' : 'View Donation'} placement="top" >
                <FlexGridItem
                  direction="column"
                  alignItems="flex-start"
                  justifyContent="center"
                  onClick={isAllocated ? handleOpenIR : handleOpenDrawer}
                  className={classes.pointer}
                >
                  <Grid item className={classes.topRow}>
                    <BigText noWrap>
                      {leftSection.toprow}
                    </BigText>
                  </Grid>
                  <Grid item>
                    <Typography component="subtitle2" variant="subtitle2" className={classes.indentText} noWrap>
                      {leftSection.middlerow}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography component="subtitle2" variant="subtitle2" className={classes.indentText} noWrap>
                      {leftSection.bottomrow}
                    </Typography>
                  </Grid>
                </FlexGridItem>
              </Tooltip>
              <FlexGridItem className={classes.progress}>
                <ProgressBar color={leftSection.progressColor} value={leftSection.progressValue} width="100%"/>
              </FlexGridItem>
              <FlexGridItem className={classes.midSection}>
                {isAllocated
                  ? <MidSectionAllocated
                      {...allocatedMidSection}
                      onOpenDrawer={handleOpenDrawer}/>
                  : <MidSectionNotAllocated
                      {... notAllocatedMidSection }
                      onOpenDrawer={handleOpenDrawer}/>
                }
              </FlexGridItem>
              <FlexGridItem
                onClick={handleOpenPortfolio}
                className={classes.pointer}
              >
                <Tooltip title="View Portfolio" >
                  {isAllocated
                    ? <RightSectionAllocated {...allocatedRightSection}/>
                    : overdueTask
                      ? <RightSectionOverdue {...overdueTask} {...program}/>
                      : <RightSectionNotAllocated {...notAllocatedRightSection}/>
                  }
                </Tooltip>
              </FlexGridItem>
            </Grid>
          </RowCardContent>
        </Card>
      </CardMain>
    </Container>
  );
});

const LinearProgress = styled.div`
  flex: 1;
  background-color: #ececec;
  height: 5px;
  position: relative;
  &:before {
    content: "";
    background-color: ${props => props.color};
    width: ${props => props.value}%;
    position: absolute;
    height: 100%;
  }
`;

const ContainerLeft = styled.div`
  position: absolute;
  right: 310px;
`;


const ContainerCenter = styled.div`
  position: absolute;
  right: 120px;
`;

const ContainerRight = styled.div`
  position: absolute;
  right: 20px;
`;

RowCardDetail.propTypes = {
  progress: PropTypes.any,
  toprow: PropTypes.string,
  middlerow: PropTypes.string,
  bottomrow: PropTypes.string,
  cash: PropTypes.number,
  goods: PropTypes.number,
  volunteerism: PropTypes.number,
  services: PropTypes.number,
  programName: PropTypes.string,
  amount: PropTypes.number,
  granteeName: PropTypes.string,
  year: PropTypes.number,
  totalBudgetInitial: PropTypes.number
};

const daysAgo = dueDate =>
  Math.floor((Date.now() - new Date(dueDate).getValue()) / 24 * 60 * 60 * 1000);

const NoLogoContainer = styled.div`
  min-width: 80px;
  max-width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  align-self: center;
  padding: 10px;
  margin: 8px;
`;

const NoLogoCircle = styled.div`
  min-width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(0,0,0,.25);
  color: #444;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  align-self: center;
  padding: 10px;
  margin: 8px;
`;

const NoLogo = () => (
  <NoLogoContainer>
    <NoLogoCircle>
      No Logo
    </NoLogoCircle>
  </NoLogoContainer>
);

const CardMain = styled(Grid).attrs({
  item: true,
  container: true,
  alignItems: 'stretch',
  justify: 'space-between',
})`
  position: relative;
  overflow: hidden;
  flex: 1 1;
`;

const FlexGridItem = styled(Grid).attrs(({ alignItems = 'center' }) => ({
  item: true,
  container: true,
  alignItems,
}))`
  flex: 1 1;
  max-width: 25%;
  min-width: 25%;
  max-height: 80px;
`;

const RowCardContent = styled(CardContent)`
  padding: 0;
  flex: 1 1;
  max-width: calc(100% - 80px);
  overflow: hidden;
  display: flex;
  align-items: stretch;
`;

const RowCardMedia = styled(CardMedia)`
  align-self: center;
`;

const BigText = styled(Typography)`
  font-size: 24px;
  font-weight: bold !important;
`;

const BlueText = styled(Typography).attrs({
  component: 'subtitle1',
  variant: 'subtitle1',
  color: 'secondary',
})`
  color: #0091ae;
`;

const CardLinearProgress = styled(LinearProgress)`
  background-color: rgba(0,0,0,.2);
  margin-bottom: 10px;
`;

const CheckboxWithIndent = styled(MuiCheckbox).attrs({
  size: 'small',
})`
  margin-left: 4px !important;
`;

const Container = styled(Grid).attrs({
  container: true,
  direction: 'row',
  justify: 'center',
  alignItems: 'center',
})`
  margin-bottom: -1px;
  margin-left: 16px;
  margin-right: 16px;
  background-color: #fff;
  box-shadow: 0 4px 4px 0 #888;
  width: calc(100% - 32px) !important;
  border-bottom: 1px solid white;
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 80px;
    right: 32px;
    height: 1px;
    background-color: rgba(0,0,0,.1);
  }
  ${({ isLastRow }) => isLastRow && `
    box-shadow: 0 4px 4px 0 #ccc;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    padding-bottom: 10px;
  `}
`;

/*  className={isLastRow
    ? classes.gridContainerMarginLast
    : isPortfolioGroup
      ? classes.gridContainerMarginPortfolio
      : classes.gridContainerMargin
  }
)*/
export const getInvitationStatus = row => {
  if (!row.invitations || row.invitations.length === 0) {
    return 'Not Sent';
  }
  const utcOffset = moment().utcOffset();
  let status = 'Sent';
  let time = moment();
  if (row?.invitationCreatedAt) {
    time = moment(row?.invitationCreatedAt).add(utcOffset, 'minutes');
  }
  if (row?.invitationUpdatedAt) {
    time = moment(row?.invitationUpdatedAt).add(utcOffset, 'minutes');
  }
  return `${status} - ${time.format('LLL')}`;
};

export default RowCardDetail;